import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AxiosError } from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Button, Icon, Input, Link, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { useBooleanState, ErrorCode } from 'libs.react';
import { AuthWrapper, SwitchWorkspaceModal } from '../../components';
import { AuthContext, AuthRoute, useEmailField } from '../../features';
export const LoginPage = () => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { getIDPConfiguration, login, mfaChallenge, redirectToDefaultPage, ssoLogin } = useContext(AuthContext);
    const navigate = useNavigate();
    const passwordRef = useRef(null);
    const { email, emailError, emailHasError, emailTouched, onEmailBlur, onEmailChange } = useEmailField();
    const [password, setPassword] = useState('');
    const [isPasswordMode, enablePasswordMode, disablePasswordMode] = useBooleanState(false);
    const [isSwitchWorkspaceModalOpen, openSwitchWorkspaceModal, closeSwitchWorkspaceModal] = useBooleanState(false);
    const [availableEntities, setAvailableEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState();
    // Redirect to MFA page when mfaChallenge is set after login
    useEffect(() => {
        if (mfaChallenge) {
            navigate(mfaChallenge.needsEnrollment ? AuthRoute.MFA_SETUP : AuthRoute.MFA_CHALLENGE);
        }
    }, [mfaChallenge, navigate]);
    const isNextDisabled = isPasswordMode ? !password : !email || emailHasError;
    const isSSOAvailable = selectedEntity && selectedEntity.loginMethods.includes('sso');
    const isPasswordAvailable = selectedEntity && selectedEntity.loginMethods.includes('password');
    const hasMultipleIdps = selectedEntity && selectedEntity.idps.length > 1;
    const showPassword = (isPasswordMode || (!isSSOAvailable && isPasswordAvailable)) && !!selectedEntity;
    const showSSO = !isPasswordMode && isSSOAvailable;
    const onPasswordChange = (event) => {
        setPassword(event.target.value);
    };
    const onNext = async () => {
        if (!isNextDisabled) {
            if (showPassword) {
                try {
                    const result = await login(email, password, selectedEntity?.id);
                    // If MFA is required, the useEffect above handles navigation to the MFA page.
                    // Only redirect to the default page when login completed fully (no MFA needed).
                    if (!result.mfaRequired) {
                        await redirectToDefaultPage();
                    }
                }
                catch (err) {
                    if (err instanceof AxiosError && err.response?.data?.errCode === ErrorCode.ACCESS_DENIED_SSO_REQUIRED) {
                        addNotification({
                            title: translate('SSO required'),
                            subtitle: translate('Password login is disabled for your account. Please use SSO.'),
                            type: 'error',
                        });
                        disablePasswordMode();
                    }
                    else {
                        throw err;
                    }
                }
            }
            else if (isSSOAvailable && !hasMultipleIdps) {
                await ssoLogin(selectedEntity, email);
            }
            else {
                validateEmail();
            }
        }
    };
    const validateEmail = async () => {
        let ipdList;
        try {
            ipdList = await getIDPConfiguration(email);
        }
        catch (error) {
            console.error('Failed to get IDP configuration:', error);
            return;
        }
        if (ipdList.length === 0) {
            addNotification({
                title: translate('Invalid email'),
                subtitle: translate('The email you entered is not associated with any account. Please check the email or sign up for a new account.'),
                type: 'error',
            });
        }
        if (ipdList.length === 1) {
            setSelectedEntity(ipdList[0]);
        }
        else {
            setAvailableEntities(ipdList);
            openSwitchWorkspaceModal();
        }
    };
    const onSignUpClick = () => {
        const url = 'https://signup.medable.com/';
        window.open(url, '_blank', 'noopener,noreferrer');
    };
    const onSelectWorkspace = (entity) => {
        setSelectedEntity(entity);
        closeSwitchWorkspaceModal();
        if (!entity.loginMethods.includes('sso') && entity.loginMethods.includes('password')) {
            passwordRef.current?.focus();
        }
    };
    return (_jsxs(AuthWrapper, { title: translate('Sign in to your account'), onSubmit: onNext, children: [_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(Input, { "data-testid": 'login-email-input', errorMessage: emailError, hasError: emailTouched && emailHasError, id: 'email', label: translate('Email'), onBlur: onEmailBlur, onChange: onEmailChange, required: true, hideRequiredIndicator: true, value: email, width: 'full', disabled: !!selectedEntity }), showPassword && (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'relative', children: [_jsx("div", { className: 'absolute right-0 top-0', children: _jsx(Link, { LinkComponent: RouterLink, to: AuthRoute.FORGOT_PASSWORD, children: translate('Forgot password?') }) }), _jsx(Input, { "data-testid": 'login-password-input', id: 'password', label: translate('Password'), onChange: onPasswordChange, ref: passwordRef, required: true, hideRequiredIndicator: true, type: 'password', value: password, width: 'full', autoFocus: true })] }), _jsx(Button, { dataTestId: 'login-button', label: translate('Sign in'), disabled: isNextDisabled })] })), showSSO && hasMultipleIdps && (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(Text, { size: 'sm', className: 'text-secondary font-medium', children: translate('Choose your identity provider') }), selectedEntity.idps.map((idp) => (_jsxs("button", { type: 'button', "data-testid": `idp-option-${idp.key}`, className: 'flex items-center justify-center gap-3 w-full rounded-lg border border-neutral-300 p-2 hover:bg-neutral-50 transition-colors', onClick: () => ssoLogin(selectedEntity, email, undefined, idp.key), children: [idp.logo ? (_jsx("img", { src: idp.logo, alt: '', className: 'h-6 w-6 object-contain' })) : (_jsx(Icon, { name: 'keySolid', size: 'sm', color: 'gray' })), _jsx("span", { className: 'text-sm font-medium', children: idp.displayName || idp.name })] }, idp.key)))] })), showSSO && !hasMultipleIdps && (_jsx(Button, { dataTestId: 'login-continue-button', label: translate('Sign in with SSO'), disabled: isNextDisabled })), !showSSO && !selectedEntity && (_jsx(Button, { dataTestId: 'login-continue-button', label: translate('Continue'), disabled: isNextDisabled })), isSSOAvailable && isPasswordAvailable && (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'flex items-center max-w-[27.5rem]', children: [_jsx("div", { className: 'border-b w-full' }), _jsx(Text, { size: 'sm', className: 'mx-4 text-secondary font-medium', children: translate('or') }), _jsx("div", { className: 'border-b w-full' })] }), showPassword ? (_jsx(Button, { type: 'button', label: translate('Sign in with SSO'), onClick: disablePasswordMode, variant: 'secondary' })) : (_jsx(Button, { type: 'button', dataTestId: 'login-with-password-button', label: translate('Sign in with password'), onClick: enablePasswordMode, variant: 'secondary' }))] })), _jsxs("div", { className: 'flex gap-1 text-sm self-center mt-10', children: [translate("Don't have an account?"), _jsx(Link, { to: '#', onClick: onSignUpClick, children: translate('Sign up') })] })] }), isSwitchWorkspaceModalOpen && availableEntities.length > 0 && (_jsx(SwitchWorkspaceModal, { entities: availableEntities, email: email, onSubmit: onSelectWorkspace, onClose: closeSwitchWorkspaceModal }))] }));
};
